// import React, { useState, useCallback } from 'react';
import {
    Package, Calendar, Receipt, MapPin, MessageSquare,
    Users, Search, Clock, CheckCircle, XCircle, AlertCircle,
    ArrowUp, ArrowDown, BarChart3
} from 'lucide-react';
import { useTranslation } from '../i18n/i18n';

const Dashboard = ({ lang }) => {
    const { t } = useTranslation(lang);

    // Stats principales
    const mainStats = [
        {
            label: t('products'),
            value: '1,234',
            icon: Package,
            color: '#7c3aed',
            description: t('totalProducts')
        },
        {
            label: t('reservations'),
            value: '89',
            icon: Calendar,
            color: '#f59e0b',
            description: t('thisMonth')
        },
        {
            label: t('unpaidInvoices'),
            value: '45',
            icon: Receipt,
            color: '#ef4444',
            description: t('pendingPayment')
        },
        {
            label: t('openTickets'),
            value: '23',
            icon: MessageSquare,
            color: '#3b82f6',
            description: t('supportRequests')
        }
    ];

    // Ventes par pays
    const salesByCountry = [
        { country: t('france'), value: '€12,450', percentage: 35, change: '+12%', isPositive: true },
        { country: t('spain'), value: '€8,920', percentage: 25, change: '+8%', isPositive: true },
        { country: t('germany'), value: '€7,150', percentage: 20, change: '-3%', isPositive: false },
        { country: t('italy'), value: '€4,280', percentage: 12, change: '+15%', isPositive: true },
        { country: t('other'), value: '€2,200', percentage: 8, change: '+5%', isPositive: true }
    ];

    // Produits les plus vendus
    const topProducts = [
        { name: 'Escapada a París', sales: 234, revenue: '€28,080', growth: '+18%', isPositive: true },
        { name: 'Business Hotel Package', sales: 189, revenue: '€22,680', growth: '+12%', isPositive: true },
        { name: 'Station de Ski Tout Compris', sales: 156, revenue: '€9,360', growth: '+25%', isPositive: true },
        { name: 'Car Rental Premium', sales: 143, revenue: '€11,440', growth: '-2%', isPositive: false },
        { name: 'Spa & Wellness Weekend', sales: 128, revenue: '€7,680', growth: '+8%', isPositive: true }
    ];

    // Utilisateurs connectés
    const connectedUsers = [
        { name: 'Jean Dupont', role: 'Admin', lastActivity: t('justNow'), status: 'online' },
        { name: 'Marie Martin', role: 'Manager', lastActivity: '2 min', status: 'online' },
        { name: 'Pierre Durant', role: 'Vendeur', lastActivity: '5 min', status: 'online' },
        { name: 'Sophie Bernard', role: 'Support', lastActivity: '8 min', status: 'online' },
        { name: 'Luc Petit', role: 'Client', lastActivity: '15 min', status: 'away' }
    ];

    // Produits les plus recherchés
    const trendingSearches = [
        { product: 'iPhone 16', searches: 1.234, growth: '+45%', isPositive: true },
        { product: 'MacBook Pro', searches: 987, growth: '+23%', isPositive: true },
        { product: 'AirPods Max', searches: 765, growth: '+67%', isPositive: true },
        { product: 'iPad Pro', searches: 654, growth: '+12%', isPositive: true },
        { product: 'Apple Pencil', searches: 543, growth: '-5%', isPositive: false }
    ];

    // Agenda
    const agendaItems = [
        { time: '09:00', title: t('teamMeeting'), participants: 5, type: 'meeting' },
        { time: '11:30', title: t('clientPresentation'), participants: 3, type: 'presentation' },
        { time: '14:00', title: t('productReview'), participants: 4, type: 'review' },
        { time: '16:00', title: t('trainingSession'), participants: 8, type: 'training' }
    ];

    // Tickets ouverts
    const openTickets = [
        { id: '#1234', subject: t('paymentIssue'), status: 'urgent', priority: 'high', time: '5 min' },
        { id: '#1233', subject: t('productQuestion'), status: 'pending', priority: 'medium', time: '1h' },
        { id: '#1232', subject: t('deliveryProblem'), status: 'pending', priority: 'high', time: '2h' },
        { id: '#1231', subject: t('technicalSupport'), status: 'resolved', priority: 'low', time: '3h' }
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case 'urgent': return '#ef4444';
            case 'pending': return '#f59e0b';
            case 'resolved': return '#10b981';
            case 'online': return '#10b981';
            case 'away': return '#f59e0b';
            default: return '#6b7280';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'urgent': return <AlertCircle size={16} />;
            case 'pending': return <Clock size={16} />;
            case 'resolved': return <CheckCircle size={16} />;
            case 'online': return <CheckCircle size={16} />;
            case 'away': return <Clock size={16} />;
            default: return <XCircle size={16} />;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'meeting': return '#7c3aed';
            case 'presentation': return '#3b82f6';
            case 'review': return '#f59e0b';
            case 'training': return '#10b981';
            default: return '#6b7280';
        }
    };

    return (
        <div style={{ padding: '12px 24px', background: '#f9fafb', minHeight: '100vh' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '8px'
            }}>
                <BarChart3 size={32} color="#7c3aed" />
                <h1 style={{
                    fontSize: '28px',
                    fontWeight: 'bold',
                    color: '#1f2937',
                }}>
                    {t('dashboard')}
                </h1>

            </div>
            <div>
                <p style={{ color: '#6b7280', fontSize: '14px' }}>
                    {t('overview')} - {new Date().toLocaleDateString(
                        lang === 'en' ? 'en-US' :
                            lang === 'es' ? 'es-ES' :
                                'fr-FR',
                        { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
                    )}
                </p>
            </div>

            {/* ========== STATS PRINCIPALES ========== */}
            <div style={{ marginBottom: '32px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                    {t('keyMetrics')}
                </h2>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '20px'
                }}>
                    {mainStats.map((stat, idx) => {
                        const Icon = stat.icon;
                        return (
                            <div
                                key={idx}
                                style={{
                                    background: 'white',
                                    padding: '24px',
                                    borderRadius: '12px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                }}
                            >
                                <div style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '12px',
                                    background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 100%)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <Icon size={28} color="white" />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontSize: '24px',
                                        fontWeight: 'bold',
                                        color: '#1f2937',
                                        marginBottom: '4px'
                                    }}>
                                        {stat.value}
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#6b7280',
                                        marginBottom: '4px'
                                    }}>
                                        {stat.label}
                                    </div>
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#9ca3af',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        {stat.description}

                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ========== VENTES PAR PAYS + PRODUITS VENDEUS ========== */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth >= 1024 ? '1fr 1fr' : '1fr',
                gap: '20px',
                marginBottom: '32px'
            }}>
                {/* Ventes par pays */}
                <div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                        {t('salesByCountry')}
                    </h2>
                    <div style={{
                        background: 'white',
                        padding: '24px',
                        borderRadius: '12px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                        {salesByCountry.map((country, idx) => (
                            <div
                                key={idx}
                                style={{
                                    padding: '12px 0',
                                    borderBottom: idx < salesByCountry.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}
                            >
                                <div style={{ width: '24px', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>
                                    {idx + 1}
                                </div>
                                <MapPin size={16} color="#6b7280" />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                                        {country.country}
                                    </div>
                                    <div style={{
                                        width: '100%',
                                        height: '6px',
                                        background: '#f3f4f6',
                                        borderRadius: '3px',
                                        overflow: 'hidden'
                                    }}>
                                        <div
                                            style={{
                                                height: '100%',
                                                background: `linear-gradient(90deg, #7c3aed 0%, #8b5cf6 100%)`,
                                                width: `${country.percentage}%`,
                                                borderRadius: '3px'
                                            }}
                                        />
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>
                                        {country.value}
                                    </div>
                                    <div style={{
                                        fontSize: '12px',
                                        color: country.isPositive ? '#10b981' : '#ef4444',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '2px'
                                    }}>
                                        {country.isPositive ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                                        {country.change}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Produits les plus vendus */}
                <div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                        {t('topSellingProducts')}
                    </h2>
                    <div style={{
                        background: 'white',
                        padding: '24px',
                        borderRadius: '12px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                        {topProducts.map((product, idx) => (
                            <div
                                key={idx}
                                style={{
                                    padding: '12px 0',
                                    borderBottom: idx < topProducts.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}
                            >
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    background: '#f3f4f6',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#7c3aed'
                                }}>
                                    {idx + 1}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>
                                        {product.name}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                        {product.sales} {t('sales')} • {product.revenue}
                                    </div>
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: product.isPositive ? '#10b981' : '#ef4444',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '2px'
                                }}>
                                    {product.isPositive ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                                    {product.growth}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ========== UTILISATEURS + RECHERCHES ========== */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth >= 1024 ? '1fr 1fr' : '1fr',
                gap: '20px',
                marginBottom: '32px'
            }}>
                {/* Utilisateurs connectés */}
                <div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                        {t('connectedUsers')} ({connectedUsers.filter(u => u.status === 'online').length})
                    </h2>
                    <div style={{
                        background: 'white',
                        padding: '20px',
                        borderRadius: '12px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                        {connectedUsers.map((user, idx) => (
                            <div
                                key={idx}
                                style={{
                                    padding: '12px 0',
                                    borderBottom: idx < connectedUsers.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}
                            >
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    background: '#f3f4f6',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#7c3aed'
                                }}>
                                    {user.name.split(' ').map(n => n[0]).join('')}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>
                                        {user.name}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                        {user.role} • {user.lastActivity}
                                    </div>
                                </div>
                                <div>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        background: `${getStatusColor(user.status)}20`,
                                        color: getStatusColor(user.status)
                                    }}>
                                        {getStatusIcon(user.status)}
                                        {user.status === 'online' ? t('online') : t('away')}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Produits recherchés */}
                <div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                        {t('trendingSearches')}
                    </h2>
                    <div style={{
                        background: 'white',
                        padding: '20px',
                        borderRadius: '12px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                        {trendingSearches.map((search, idx) => (
                            <div
                                key={idx}
                                style={{
                                    padding: '12px 0',
                                    borderBottom: idx < trendingSearches.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}
                            >
                                <Search size={16} color="#6b7280" />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>
                                        {search.product}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                        {search.searches.toLocaleString()} {t('searches')}
                                    </div>
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: search.isPositive ? '#10b981' : '#ef4444',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '2px'
                                }}>
                                    {search.isPositive ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                                    {search.growth}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ========== AGENDA + TICKETS ========== */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth >= 1024 ? '1fr 1fr' : '1fr',
                gap: '20px'
            }}>
                {/* Agenda */}
                <div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                        {t('agenda')} - {new Date().toLocaleDateString(
                            lang === 'en' ? 'en-US' :
                                lang === 'es' ? 'es-ES' :
                                    'fr-FR',
                            { day: 'numeric', month: 'short' }
                        )}
                    </h2>
                    <div style={{
                        background: 'white',
                        padding: '20px',
                        borderRadius: '12px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                        {agendaItems.map((item, idx) => (
                            <div
                                key={idx}
                                style={{
                                    padding: '16px 0',
                                    borderBottom: idx < agendaItems.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    display: 'flex',
                                    gap: '12px',
                                    alignItems: 'flex-start'
                                }}
                            >
                                <div style={{
                                    width: '50px',
                                    height: '50px',
                                    borderRadius: '10px',
                                    background: `${getTypeColor(item.type)}20`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <div style={{
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        color: getTypeColor(item.type)
                                    }}>
                                        {item.time}
                                    </div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                                        {item.title}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Users size={12} />
                                        {item.participants} {t('participants')}
                                    </div>
                                </div>
                                <div style={{
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    background: `${getTypeColor(item.type)}20`,
                                    color: getTypeColor(item.type),
                                    textTransform: 'capitalize'
                                }}>
                                    {t(item.type)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Tickets support */}
                <div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                        {t('supportTickets')}
                    </h2>
                    <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '80px 1fr 100px 80px 80px',
                            padding: '16px 20px',
                            background: '#f9fafb',
                            borderBottom: '1px solid #e5e7eb',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#6b7280',
                            textTransform: 'uppercase'
                        }}>
                            <div>ID</div>
                            <div>{t('subject')}</div>
                            <div>{t('status')}</div>
                            <div>{t('priority')}</div>
                            <div>{t('time')}</div>
                        </div>
                        {openTickets.map((ticket, idx) => (
                            <div
                                key={idx}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '80px 1fr 100px 80px 80px',
                                    padding: '16px 20px',
                                    borderBottom: idx < openTickets.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    alignItems: 'center',
                                    transition: 'background 0.2s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                                <div style={{ fontSize: '12px', fontWeight: '600', color: '#7c3aed' }}>
                                    {ticket.id}
                                </div>
                                <div style={{ fontSize: '13px', color: '#1f2937' }}>
                                    {ticket.subject}
                                </div>
                                <div>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        background: `${getStatusColor(ticket.status)}20`,
                                        color: getStatusColor(ticket.status)
                                    }}>
                                        {getStatusIcon(ticket.status)}
                                        {ticket.status === 'urgent' ? t('urgent') : ticket.status === 'pending' ? t('pending') : t('resolved')}
                                    </span>
                                </div>
                                <div>
                                    <span style={{
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        background: ticket.priority === 'high' ? '#fee' : ticket.priority === 'medium' ? '#fef3c7' : '#f0fdf4',
                                        color: ticket.priority === 'high' ? '#dc2626' : ticket.priority === 'medium' ? '#f59e0b' : '#10b981'
                                    }}>
                                        {ticket.priority === 'high' ? t('high') : ticket.priority === 'medium' ? t('medium') : t('low')}
                                    </span>
                                </div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                    {ticket.time}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;